import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Pin } from 'lucide-react';

/**
 * PinUntilDialog
 *  - Lets admins pick the date and time a forum pin should expire
 *  - Calls onConfirm(isoString) when the admin clicks "Pin until..."
 */
export default function PinUntilDialog({ open, onOpenChange, onConfirm, postTitle }) {
  // Default expiry: 7 days from now at 23:59
  const initialDate = (() => { const d = new Date(); d.setDate(d.getDate() + 7); return d; })();
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState('23:59');
  const [submitting, setSubmitting] = useState(false);

  const buildExpiry = () => {
    if (!date) return null;
    const [h, m] = (time || '23:59').split(':').map(n => parseInt(n, 10));
    const d = new Date(date);
    d.setHours(Number.isFinite(h) ? h : 23, Number.isFinite(m) ? m : 59, 0, 0);
    return d;
  };
  const expiry = buildExpiry();
  const isFuture = expiry && expiry.getTime() > Date.now();

  const handleConfirm = async () => {
    if (!expiry || !isFuture) return;
    setSubmitting(true);
    try {
      await onConfirm(expiry.toISOString());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pin-until-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pin className="h-4 w-4 text-amber-500" /> Pin post to top
          </DialogTitle>
        </DialogHeader>
        {postTitle && (
          <p className="text-sm text-slate-600 -mt-2 mb-1 line-clamp-2 italic">"{postTitle}"</p>
        )}
        <p className="text-xs text-slate-500 mb-2">Choose when the pin should expire. Once that time passes, the post automatically returns to the regular feed order.</p>

        <div className="rounded-lg border border-slate-200 p-2 flex justify-center">
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
            data-testid="pin-calendar"
          />
        </div>

        <div className="flex items-center gap-2 mt-2">
          <label className="text-xs text-slate-500 font-medium w-16">Time</label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="border border-slate-200 rounded-md px-3 py-1.5 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-brand-navy/40"
            data-testid="pin-time-input"
          />
        </div>

        {expiry && (
          <p className={`text-xs mt-2 ${isFuture ? 'text-emerald-700' : 'text-rose-600'}`} data-testid="pin-expiry-summary">
            {isFuture
              ? `Pinned until ${expiry.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
              : 'Selected time is in the past — please pick a future time.'}
          </p>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="pin-cancel">Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={!isFuture || submitting}
            className="bg-gradient-to-r from-amber-500 to-amber-600 text-white"
            data-testid="pin-confirm"
          >
            {submitting ? 'Pinning…' : 'Pin to top'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
